import { useState, useEffect } from 'react';
import { adminGet } from '../lib/api';

interface Metrics {
  contractsToday: number;
  contractsWeek: number;
  avgProcessingSeconds: number;
  successRate: number;
  activeReviews: number;
}

interface Jurisdiction { state: string; count: number; pct: number; }
interface ThroughputDay { date: string; count: number; }
interface Failure { id: string; posture: string; jurisdiction: string; status: string; failedStage: number; createdAt: number; }

const FALLBACK_METRICS: Metrics = { contractsToday: 47, contractsWeek: 312, avgProcessingSeconds: 43, successRate: 96.2, activeReviews: 3 };
const FALLBACK_JURISDICTIONS: Jurisdiction[] = [
  { state: 'CA', count: 142, pct: 33 }, { state: 'TX', count: 98, pct: 23 },
  { state: 'FL', count: 87, pct: 20 }, { state: 'NY', count: 64, pct: 15 }, { state: 'OH', count: 41, pct: 9 },
];
const FALLBACK_THROUGHPUT: ThroughputDay[] = [
  { date: '2026-03-17', count: 28 }, { date: '2026-03-18', count: 35 }, { date: '2026-03-19', count: 31 },
  { date: '2026-03-20', count: 42 }, { date: '2026-03-21', count: 38 }, { date: '2026-03-22', count: 51 },
  { date: '2026-03-23', count: 47 },
];

const STATE_NAMES: Record<string, string> = { CA: 'California', TX: 'Texas', FL: 'Florida', NY: 'New York', OH: 'Ohio', AZ: 'Arizona', GA: 'Georgia', TN: 'Tennessee', NM: 'New Mexico', AR: 'Arkansas', AL: 'Alabama' };

export function TriageAnalytics() {
  const [metrics, setMetrics] = useState<Metrics>(FALLBACK_METRICS);
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>(FALLBACK_JURISDICTIONS);
  const [throughput, setThroughput] = useState<ThroughputDay[]>(FALLBACK_THROUGHPUT);
  const [failures, setFailures] = useState<Failure[]>([]);

  useEffect(() => {
    (async () => {
      const [mResp, jResp, tResp, fResp] = await Promise.all([
        adminGet('/api/admin/analytics/overview'),
        adminGet('/api/admin/analytics/jurisdictions'),
        adminGet('/api/admin/analytics/throughput?days=7'),
        adminGet('/api/admin/analytics/failures?limit=5'),
      ]);
      if (mResp.ok && mResp.data) setMetrics(mResp.data as Metrics);
      if (jResp.ok && (jResp.data as { jurisdictions: Jurisdiction[] })?.jurisdictions?.length) setJurisdictions((jResp.data as { jurisdictions: Jurisdiction[] }).jurisdictions);
      if (tResp.ok && (tResp.data as { days: ThroughputDay[] })?.days?.length) setThroughput((tResp.data as { days: ThroughputDay[] }).days);
      if (fResp.ok && (fResp.data as { failures: Failure[] })?.failures) setFailures((fResp.data as { failures: Failure[] }).failures);
    })();
  }, []);

  const maxThroughput = Math.max(...throughput.map(d => d.count), 1);

  const metricCards = [
    { label: 'Contracts Today', value: String(metrics.contractsToday), change: `${metrics.contractsWeek} this week` },
    { label: 'Avg Processing', value: `${metrics.avgProcessingSeconds}s`, change: '' },
    { label: 'Success Rate', value: `${metrics.successRate}%`, change: '7-day rolling' },
    { label: 'Active Reviews', value: String(metrics.activeReviews), change: 'right now' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Triage Analytics</h1>
          <p className="text-sm text-dark-400 mt-1">Last updated: {new Date().toLocaleString()}</p>
        </div>
        <button onClick={() => window.location.reload()}
          className="px-3 py-1.5 text-sm bg-rights-500/10 text-rights-500 rounded-lg border border-rights-500/20">
          Refresh
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metricCards.map(m => (
          <div key={m.label} className="bg-dark-700 rounded-2xl border border-dark-650/50 p-5">
            <p className="text-xs text-dark-400 uppercase tracking-wider">{m.label}</p>
            <p className="text-2xl font-display font-bold text-white mt-1">{m.value}</p>
            {m.change && <p className="text-xs text-dark-500 mt-1">{m.change}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Throughput Chart */}
        <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
          <h3 className="text-white font-medium mb-4">Pipeline Throughput (7-Day)</h3>
          <div className="h-48 flex items-end gap-2">
            {throughput.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-[10px] text-dark-400 font-mono">{d.count}</div>
                <div className="w-full rounded-t bg-rights-500/30 relative transition-all"
                     style={{ height: `${(d.count / maxThroughput) * 100}%` }}>
                  <div className="absolute inset-x-0 top-0 h-0.5 bg-rights-500 rounded-full"
                       style={{ boxShadow: '0 0 8px rgba(0,229,255,0.5)' }} />
                </div>
                <div className="text-[9px] text-dark-500">{d.date.split('-')[2]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Jurisdiction Distribution */}
        <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
          <h3 className="text-white font-medium mb-4">Jurisdiction Distribution</h3>
          <div className="space-y-3">
            {jurisdictions.map(j => (
              <div key={j.state}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-dark-300">{STATE_NAMES[j.state] || j.state} ({j.state})</span>
                  <span className="text-dark-400 font-mono">{j.count}</span>
                </div>
                <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
                  <div className="h-full bg-rights-500/60 rounded-full transition-all" style={{ width: `${j.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Failures */}
      {failures.length > 0 && (
        <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
          <h3 className="text-white font-medium mb-4">Recent Failures</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-650/50">
                <th className="px-3 py-2 text-left text-xs font-medium text-dark-400 uppercase">Pipeline</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-dark-400 uppercase">Failed Stage</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-dark-400 uppercase">State</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-650/30">
              {failures.map(f => (
                <tr key={f.id} className="hover:bg-dark-750/50">
                  <td className="px-3 py-2 text-sm text-dark-300 font-mono">{f.id.substring(0, 16)}...</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs bg-dark-800 text-dark-300 rounded">Stage {f.failedStage}</span></td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs bg-severity-high/10 text-severity-high rounded">{f.status}</span></td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs bg-dark-800 text-dark-400 rounded">{f.jurisdiction}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
